// Maps an Ask-AI verbatim citation to a transcript segment index and embeds it in a URL. Pure + framework-free.
export const VERBATIM_PARAM = "verbatim";
// Enough to locate the start segment; keeps the URL (and any logs) bounded.
export const VERBATIM_PARAM_MAX_LEN = 120;
// Leading-word count tried when the full citation has no exact match.
const FALLBACK_LEADING_WORDS = 4;
// Lowercase + collapse non-alphanumerics to single spaces so matching survives case/punctuation/whitespace across scripts.
export const normalizeForMatch = (text) => text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
// Flat, start-ordered index of the segment where `citation` begins, or null if not confidently located.
export const findFlatSegmentIndexByCitation = (citation, segmentTexts) => {
    const needle = normalizeForMatch(citation);
    if (!needle || segmentTexts.length === 0)
        return null;
    // Combined normalized transcript + the start offset of each segment within it.
    const offsets = [];
    let combined = "";
    for (const text of segmentTexts) {
        const norm = normalizeForMatch(text);
        // Separate only before non-empty segments so empty ones don't inject double spaces the needle can't match.
        if (norm && combined)
            combined += " ";
        offsets.push(combined.length);
        combined += norm;
    }
    const locate = (fragment) => {
        const at = combined.indexOf(fragment);
        if (at < 0)
            return null;
        let index = 0;
        for (let i = 0; i < offsets.length && offsets[i] <= at; i++)
            index = i;
        return index;
    };
    const direct = locate(needle);
    if (direct !== null)
        return direct;
    // Only worth retrying when truncation yields a shorter fragment than the citation that already missed.
    const words = needle.split(" ");
    return words.length > FALLBACK_LEADING_WORDS ? locate(words.slice(0, FALLBACK_LEADING_WORDS).join(" ")) : null;
};
// Append the (length-capped) citation to `url` as the verbatim deep-link param.
export const appendVerbatimParam = (url, citation, origin = window.location.origin) => {
    const trimmed = citation.trim();
    if (!trimmed)
        return url;
    try {
        const parsed = new URL(url, origin);
        parsed.searchParams.set(VERBATIM_PARAM, trimmed.slice(0, VERBATIM_PARAM_MAX_LEN));
        return parsed.toString();
    }
    catch {
        return url;
    }
};
// The URL a verbatim source opens: EC sources carry the citation deep-link; others open as-is.
export const verbatimSourceUrl = (source) => source.externalContent ? appendVerbatimParam(source.responseSourceUrl, source.citation) : source.responseSourceUrl;
